import React, { useState, useEffect } from "react";
import {
	GoogleMap,
	useJsApiLoader,
	Marker,
	InfoWindow,
	Polyline,
} from "@react-google-maps/api";
import { getMetroType } from "../utilities/utilities";
import { StopData } from "../../../interfaces/routes/routeData";

interface IProps {
	activeSchedule: any,
	routeId: number,
	stopDetails: StopData,
}
interface LatLng {
	lat: number,
	lng: number,
}

function RouteMap({ activeSchedule, routeId, stopDetails }: IProps) {
	const { isLoaded } = useJsApiLoader({
		id: "google-map-script",
		googleMapsApiKey: "AIzaSyC4FnVgNok4coXQYxGT7mHjsMBzjUQoEaY",
	});
	const [scheduleData, updateScheduleData] = useState(activeSchedule);
	const [map, setMap] = useState<any>(null);
	const [activeMarkerId, setActiveMarkerId] = useState<string | null>(null);
	const [traceRoute, updateTraceRoute] = useState<LatLng[]>([]);
	const [mapZoom, updateMapZoom] = useState(5);
	const container = document.getElementById('schedule-map') as HTMLElement;
	const detailPage = container.dataset.detailPage;
	const metroId = getMetroType(routeId);
	const mapStyles = {
		width: "100%",
		height: "510px",
	};
	const markerGreenDot = require("../../../images/markers/stopG.png");
	const timePointLabel = require(`../../../images/markers/tp.png`);
	const pdfLink = "/docs/default-source/scheduled-routes/" + routeId.toString().padStart(3, '0') + ".pdf";

	// allows scroll wheel to zoom map
	const mapOptions = {
		gestureHandling: "greedy",
	};

	const infoOptions = {
		maxWidth: 250,
	};
	
	const setBounds = (map: any) => {
		const bounds = new window.google.maps.LatLngBounds();
		activeSchedule.MapPoints.forEach((item: any) => {
			const latLng = {
				lat: item.Latitude,
				lng: item.Longitude,
			};
			bounds.extend(latLng);
		});
		map.fitBounds(bounds);
		setMap(map);
	};

	const handleMarkerClick = (id: string) => {
		setActiveMarkerId(id);
	};

	const handleInfoClose = () => {
		setActiveMarkerId(null);
	};

	const onLoad = React.useCallback(function callback(map: any) {
		setBounds(map);
	}, []);

	const onUnmount = React.useCallback(function callback(map: any) {
		setMap(null);
	}, []);

	useEffect(() => {
		const paths: LatLng[] = [];
		scheduleData.RoutePoints.forEach((item: any) => {
			paths.push({
				lat: item.Latitude,
				lng: item.Longitude,
			});
		});
		// the original trace route wont refresh unless theres a timeout
		// possible issue with polyline component not updating?
		// https://github.com/fullstackreact/google-maps-react/issues/501
		setTimeout(() => {
			updateTraceRoute(paths);
		},100)
	},[scheduleData]);
	
	useEffect(() => {
		updateScheduleData(activeSchedule);
		if (map) {
			setBounds(map);
		}
	}, [activeSchedule]);

	useEffect(() => {
		// when you click on a service alert, zoom to that location
		if (stopDetails && stopDetails.id !== '') {
			map.setZoom(15);
			map.setCenter({
				lat: stopDetails.lat,
				lng: stopDetails.lng,
			});
		}
	},[stopDetails]);

	return isLoaded ? (	
		<div className="route-map">
			<div className="route-map__links">
				<a href={pdfLink} className="route-map__link">
					<svg className="route-map__icon" aria-hidden="true">
						<use href="#icon-doc-download"></use>
					</svg>
					Printable PDF
				</a>
				<a href={`https://app.capmetro.org/AspSingleForms/SingleForm/turnbyturndescription?data=${routeId}`} className="route-map__link">
					<svg className="route-map__icon" aria-hidden="true">
						<use href="#icon-turn-by-turn"></use>
					</svg>
					Turn-by-Turn
				</a>
				
			</div>
			<div className="route-map__legend">
				<img src={markerGreenDot} className="route-map__legend-image" alt="stops marker" />
				<span className="route-map__legend-label">Stops</span>
				<img src={timePointLabel} className="route-map__legend-image" alt="time table stop" />
				<span className="route-map__legend-label">Time-table stops</span>
			</div>
			<GoogleMap
				mapContainerStyle={mapStyles}
				zoom={mapZoom}
				onLoad={onLoad}
				onUnmount={onUnmount}
				options={mapOptions}
			>
				<Polyline
					path={traceRoute}
				/>
				{scheduleData.MapPoints.map((item: any) => {
					const latlng = {
						lat: item.Latitude,
						lng: item.Longitude,
					};
					const flagColor = (metroId === 5 || metroId === 9)
						? '-red'
						: (metroId === 6)
						? '-ut'
						: '';
					let markerImage = "";
					try {
						markerImage = item.TimePoint
							? require(`../../../images/timestops/${item.TimepointNumber}${flagColor}.gif`)
							: markerGreenDot;
					}
					catch {
						markerImage = item.TimePoint
							? require(`../../../images/timestops/default.gif`)
							: markerGreenDot;
					}
					const icon = {
						url: markerImage,
						origin: new window.google.maps.Point(0, 0),
						anchor: item.TimePoint
							? new window.google.maps.Point(9, 13)
							: new window.google.maps.Point(6, 7),
					};
					const markerId = item.StopId;
					const zIndex = item.TimePoint ? 5 : 1;
					const direction = activeSchedule.Direction.charAt(0).toLowerCase();
					return (
						<Marker
							zIndex={zIndex}
							key={item.StopId}
							icon={icon}
							position={latlng}
							onClick={() => handleMarkerClick(markerId)}
						>
							{markerId === activeMarkerId ? (
								<InfoWindow options={infoOptions} onCloseClick={handleInfoClose}>
									<div className="stop-info">
										<p className="stop-info__label">
											Stop ID:{" "}
											<span className="stop-info__value">
												{markerId}
											</span>
										</p>

										<p className="stop-info__label">
											Stop Name:{" "}
											<span className="stop-info__value">
												{item.StopName}
											</span>
										</p>
										<a
											href={`${detailPage}/${markerId}`}
											className="stop-info__link"
										>
											Click here for Schedule Data
											<span className="visually-hidden">
												opens in a new window
											</span>
										</a>
									</div>
								</InfoWindow>
							) : (
								""
							)}
						</Marker>
					);
				})}
			</GoogleMap>
		</div>
	) : (
		<></>
	);
}
export default RouteMap;
